import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Loader2,
  Merge,
  X,
  CheckCircle2,
  Calendar,
  Link2,
  Eye,
  Users,
  FileText,
} from "lucide-react";
import { Button } from "../../components/ui/Button";
import { ProviderLabel } from "../../components/ui/ProviderLabel";
import { Pagination } from "../../components/ui/Pagination";
import { Badge } from "../../components/ui/Badge";
import { EntityLink } from "../../components/ui/EntityLink";
import { EventStatusBadge } from "../../components/ui/EventStatusBadge";
import { CitySelect } from "../../components/ui/CitySelect";
import { SearchFilterForm, useSearchFilter } from "../../components/ui/SearchFilterForm";
import { Modal, ModalContent, ModalFooter } from "../../components/ui/Modal";
import {
  ComparisonGrid,
  DuplicateReviewCard,
  EventArtists,
  EventCardSection,
  EventDescription,
  EventOccurrences,
  EventUrls,
  OnlyMarkDuplicateToggle,
  formatDuplicateDate as formatDate,
} from "../../components/ui/DuplicateReview";
import { eventsApi } from "../../utils/api";
import { displayLabel } from "../../utils/helpers";
import FlaggedDuplicatesView from "./FlaggedDuplicatesView";
import EventDisplayImage from "./EventDisplayImage";

const hasPendingEvent = (group) =>
  // A snapshot event gone from the index keeps its detection-time status; it pends nothing.
  group.events?.some((event) => event.display_status !== "deleted" && event.status?.toLowerCase() === "pending");

function OriginDot({ origin }) {
  return (
    <span
      className={`inline-block h-2 w-2 rounded-full shrink-0 ${
        origin === "source" ? "bg-yellow-400" : "bg-blue-400"
      }`}
      title={origin === "source" ? "From duplicate event" : "From canonical event"}
    />
  );
}

// Preview panel shown inside the merge modal after loading
function MergePreview({ preview, occurrenceName = "" }) {
  const isLoading = !preview;
  const newOccs = preview?.occurrences_from_source ?? 0;
  const newUrls = preview?.urls_from_source ?? 0;
  const normalizedOccurrenceName = occurrenceName.trim();

  return (
    <div className="mt-4 space-y-4">
      {/* What will happen summary */}
      <div className="rounded-lg bg-muted/50 px-4 py-3 text-sm space-y-1.5">
        <p className="font-medium text-foreground">What will happen</p>
        {!isLoading && (
          <>
            <p className="text-muted-foreground">
              <span className="font-medium text-foreground">&ldquo;{preview.source_title}&rdquo;</span> will be
              flagged as <code className="text-xs bg-muted px-1 py-0.5 rounded">duplicate</code> and hidden from
              listings.
            </p>
            <p className="text-muted-foreground">
              <span className="font-medium text-foreground">&ldquo;{preview.canonical_title}&rdquo;</span> will
              be the surviving canonical event.
            </p>
            <ul className="list-disc list-inside text-muted-foreground space-y-0.5 pt-1">
              <li>
                {preview.occurrences_from_target} occurrence{preview.occurrences_from_target !== 1 ? "s" : ""} already
                on canonical
                {!preview.merge_occurrences ? (
                  <span className="text-muted-foreground"> (occurrences will not be absorbed)</span>
                ) : newOccs > 0 ? (
                  <span className="text-yellow-700"> + {newOccs} new absorbed from duplicate</span>
                ) : (
                  <span className="text-muted-foreground"> (no new occurrences to absorb)</span>
                )}
              </li>
              <li>
                {preview.urls.filter((u) => u.origin === "target").length} ticket link
                {preview.urls.filter((u) => u.origin === "target").length !== 1 ? "s" : ""} already on canonical
                {newUrls > 0 ? (
                  <span className="text-yellow-700"> + {newUrls} new absorbed from duplicate</span>
                ) : null}
              </li>
            </ul>
          </>
        )}
        {isLoading && (
          <p className="flex items-center gap-2 text-muted-foreground">
            <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading preview…
          </p>
        )}
      </div>

      {/* Occurrences table */}
      {!isLoading && preview.occurrences.length > 0 && (
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2 flex items-center gap-1.5">
            <Calendar className="h-3.5 w-3.5" />
            Merged occurrences ({preview.occurrences.length})
          </p>
          {/* The mask fades the right edge as a horizontal-scroll hint below lg. */}
          <div className="overflow-x-auto rounded-lg border border-border [mask-image:linear-gradient(to_right,black_calc(100%_-_2rem),transparent)] lg:[mask-image:none]">
            <table className="w-full text-xs">
              <thead className="bg-muted/40">
                <tr>
                  <th className="text-left px-3 py-2 font-medium text-muted-foreground">Date</th>
                  <th className="text-left px-3 py-2 font-medium text-muted-foreground">Time</th>
                  <th className="text-left px-3 py-2 font-medium text-muted-foreground">Venue</th>
                  <th className="text-left px-3 py-2 font-medium text-muted-foreground">Label</th>
                  <th className="text-left px-3 py-2 font-medium text-muted-foreground">Status</th>
                  <th className="text-left px-3 py-2 font-medium text-muted-foreground">Origin</th>
                </tr>
              </thead>
              <tbody>
                {preview.occurrences.map((occ, i) => (
                  <tr key={i} className={`border-t border-border ${occ.origin === "source" ? "bg-yellow-50/60" : ""}`}>
                    <td className="px-3 py-2">{formatDate(occ.local_date)}</td>
                    <td className="px-3 py-2">{occ.local_time || "—"}</td>
                    <td className="px-3 py-2 max-w-[160px] truncate">{occ.venue_name || "—"}</td>
                    <td className="px-3 py-2 max-w-[160px] truncate">
                      {occ.name || (occ.origin === "source" ? normalizedOccurrenceName : "") || "—"}
                    </td>
                    <td className="px-3 py-2">{occ.status || "—"}</td>
                    <td className="px-3 py-2">
                      <span className="flex items-center gap-1.5">
                        <OriginDot origin={occ.origin} />
                        {occ.origin === "source" ? "absorbed" : "existing"}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Ticket links */}
      {!isLoading && preview.urls.length > 0 && (
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2 flex items-center gap-1.5">
            <Link2 className="h-3.5 w-3.5" />
            Merged ticket links ({preview.urls.length})
          </p>
          <div className="space-y-1">
            {preview.urls.map((u, i) => (
              <div key={i} className="flex items-center gap-2 text-xs">
                <OriginDot origin={u.origin} />
                <span className="text-muted-foreground w-20 shrink-0">{u.provider || "unknown"}</span>
                <a
                  href={u.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="truncate text-blue-600 hover:underline max-w-xs"
                >
                  {u.url}
                </a>
                {u.origin === "source" && (
                  <Badge variant="warning" className="text-[10px] py-0 px-1">new</Badge>
                )}
              </div>
            ))}
          </div>
          <p className="mt-2 text-[11px] text-muted-foreground flex items-center gap-1">
            <OriginDot origin="target" /> existing on canonical &nbsp;
            <OriginDot origin="source" /> absorbed from duplicate
          </p>
        </div>
      )}
    </div>
  );
}

function EventCard({ event, isSelected, onSelect, selectLabel, selectLoading = false, selectDisabled = false }) {
  return (
    <div
      className={`min-w-0 rounded-lg border p-4 transition-colors ${
        isSelected ? "border-primary bg-primary/5" : "border-border bg-card"
      }`}
    >
      {event.image && (
        <EventDisplayImage
          src={event.image}
          alt={event.title}
          imageResolutionStatus={event.image_resolution_status}
          className="mb-3 h-28 w-full rounded"
        />
      )}
      <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">
        {event.city}
      </p>
      <div className="mb-2">
        <h4 className="font-semibold text-sm leading-snug">{displayLabel(event.preferred_title, event.title)}</h4>
        <ProviderLabel preferred={event.preferred_title} original={event.title} />
      </div>
      <div className="space-y-1 text-xs text-muted-foreground mb-3">
        <p>
          {event.local_date
            ? `${formatDate(event.local_date)}${event.local_time ? ` · ${event.local_time}` : ""}`
            : "Date TBA"}
        </p>
        {event.venue_name && (
          <p>
            <EntityLink type="venue" id={event.venue_id} newTab>{event.venue_name}</EntityLink>
          </p>
        )}
        <EventStatusBadge status={event.display_status || event.status} />
      </div>
      <EventCardSection icon={Users} title="Artists">
        <EventArtists artists={event.artists || []} />
      </EventCardSection>
      <EventCardSection icon={FileText} title="Description">
        <EventDescription description={event.description} />
      </EventCardSection>
      <EventCardSection icon={Calendar} title="Occurrences">
        <EventOccurrences occurrences={event.occurrences || []} />
      </EventCardSection>
      <EventCardSection icon={Link2} title="Event URLs">
        <EventUrls urls={event.urls || []} />
      </EventCardSection>
      {onSelect && (
        <Button
          variant={isSelected ? "default" : "outline"}
          size="sm"
          className="w-full text-xs"
          onClick={() => onSelect(event.id)}
          loading={selectLoading}
          disabled={selectDisabled}
        >
          {isSelected && <CheckCircle2 className="mr-1 h-3.5 w-3.5" />}
          {selectLabel}
        </Button>
      )}
    </div>
  );
}

function DuplicateGroup({
  group,
  onActionSuccess,
  onOpenEventEdit,
  resolutionBusy,
  isResolutionBusy,
  beginResolution,
  endResolution,
}) {
  const [markDifferentLoading, setMarkDifferentLoading] = useState(false);
  const [markDifferentError, setMarkDifferentError] = useState(null);

  // Merge modal state
  const [mergeModalOpen, setMergeModalOpen] = useState(false);
  const [mergeStep, setMergeStep] = useState("pick"); // "pick" | "preview"
  const [canonicalId, setCanonicalId] = useState(null);
  const [mergeOccurrences, setMergeOccurrences] = useState(true);
  const [editedOccurrenceName, setEditedOccurrenceName] = useState("");
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [mergeLoading, setMergeLoading] = useState(false);
  const [mergePhase, setMergePhase] = useState("idle"); // "idle" | "merging" | "opening" | "open-failed"
  const [mergeSuccessMessage, setMergeSuccessMessage] = useState("");
  const [mergeError, setMergeError] = useState(null);
  const previewRequestRef = useRef(0);
  const editorOpenRequestRef = useRef(false);

  const [evA, evB] = group.events;
  const resolutionKey = group.id || [evA.id, evB.id].sort().join("|");
  const mergeProgressCopy = {
    merging: {
      title: "Merging events…",
      description: "The duplicate is being resolved. The editor will open when the merge is complete.",
      status: "Merging events…",
      detail: "Occurrences and ticket links are being resolved. This view will remain blocked until the merge finishes.",
    },
    opening: {
      title: "Opening merged event…",
      description: "The merge is complete. Loading the merged event into the editor.",
      status: "Loading the event editor…",
      detail: "The merge is saved. The existing event editor will appear as soon as its data is ready.",
    },
  }[mergePhase];
  const mergeModalTitle = mergePhase === "open-failed"
    ? "Merge completed"
    : mergeProgressCopy?.title || (mergeStep === "pick" ? "Merge events — pick the canonical one" : "Merge preview");
  const mergeModalDescription = mergePhase === "open-failed"
    ? "The duplicate was resolved, but the editor still needs to be opened."
    : mergeProgressCopy?.description || (mergeStep === "pick"
      ? "The canonical event keeps its title, images, and description. Occurrences and ticket links from both events will be merged into it. The other event will be flagged as duplicate and hidden."
      : "Review what will change before confirming.");

  // ── Merge flow ─────────────────────────────────────────────────────────────

  const openMergeModal = () => {
    if (isResolutionBusy()) return;

    previewRequestRef.current += 1;
    setCanonicalId(group.canonical_hint_id || group.events[0]?.id || null);
    setPreview(null);
    setPreviewLoading(false);
    setMergeStep("pick");
    setMergeOccurrences(true);
    setEditedOccurrenceName("");
    setMergePhase("idle");
    setMergeSuccessMessage("");
    setMergeError(null);
    setMergeModalOpen(true);
  };

  const handleCanonicalSelect = (eventId) => {
    if (previewLoading || isResolutionBusy()) return;

    previewRequestRef.current += 1;
    setCanonicalId(eventId);
    setPreview(null);
    setMergeError(null);
  };

  const handleLoadPreview = async () => {
    const previewCanonicalId = canonicalId;
    const sourceId = group.events.find((e) => e.id !== previewCanonicalId)?.id;
    if (!sourceId) return;

    const requestId = ++previewRequestRef.current;
    setPreviewLoading(true);
    setMergeError(null);
    try {
      const data = await eventsApi.getMergePreview(sourceId, previewCanonicalId, mergeOccurrences);
      if (requestId !== previewRequestRef.current) return;

      setPreview(data);
      // Pre-filled, never auto-applied: the admin confirms or clears it. Unnamed tiers sharing a
      // date/time and provider are collapsed by the public dedup (#599), so "none" must be a choice.
      setEditedOccurrenceName(data.suggested_occurrence_name || "");
      setMergeStep("preview");
    } catch (e) {
      if (requestId !== previewRequestRef.current) return;
      setMergeError(e?.response?.data?.detail || "Could not load preview");
    } finally {
      if (requestId === previewRequestRef.current) setPreviewLoading(false);
    }
  };

  const handleOpenMergedEvent = async (
    eventId = preview?.target_event_id,
    successMessage = mergeSuccessMessage
  ) => {
    if (editorOpenRequestRef.current) return;

    editorOpenRequestRef.current = true;
    setMergeLoading(true);
    setMergePhase("opening");
    setMergeError(null);

    try {
      if (!onOpenEventEdit) throw new Error("Event editor is unavailable");
      if (!eventId) throw new Error("Merged event is unavailable");
      await onOpenEventEdit(eventId);
    } catch (e) {
      setMergePhase("open-failed");
      setMergeError(e?.response?.data?.detail || "The events were merged, but the event editor could not be opened.");
      setMergeLoading(false);
      editorOpenRequestRef.current = false;
      return;
    }

    setMergeModalOpen(false);
    setMergeLoading(false);
    editorOpenRequestRef.current = false;
    await onActionSuccess?.(successMessage);
  };

  const handleConfirmMerge = async () => {
    const sourceId = preview?.source_event_id;
    const targetId = preview?.target_event_id;
    const requestKey = `${resolutionKey}:merge`;
    if (!sourceId || !targetId || !beginResolution(requestKey)) return;

    const successMessage = `Merged "${preview.source_title}" into "${preview.canonical_title}"`;
    setMergeLoading(true);
    setMergePhase("merging");
    setMergeSuccessMessage(successMessage);
    setMergeError(null);

    try {
      // Only while the label field is on screen: it is pre-filled with a suggestion, and a
      // hidden one must never be applied behind the admin's back.
      const labelShown = preview.merge_occurrences && preview.occurrences_from_source > 0;
      await eventsApi.mergeInto(
        sourceId,
        targetId,
        preview.merge_occurrences,
        (labelShown && editedOccurrenceName.trim()) || null
      );
    } catch (e) {
      setMergePhase("idle");
      setMergeError(e?.response?.data?.detail || "Merge failed");
      setMergeLoading(false);
      endResolution(requestKey);
      return;
    }

    try {
      await handleOpenMergedEvent(targetId, successMessage);
    } finally {
      endResolution(requestKey);
    }
  };

  const handleMergeModalOpenChange = async (open) => {
    if (!open && (mergeLoading || editorOpenRequestRef.current)) return;

    if (!open) {
      previewRequestRef.current += 1;
      setPreviewLoading(false);
    }
    setMergeModalOpen(open);
    if (!open && mergePhase === "open-failed") {
      setMergePhase("idle");
      setMergeError(null);
      await onActionSuccess?.(mergeSuccessMessage);
    }
  };

  const handleMarkDifferent = async () => {
    const requestKey = `${resolutionKey}:different`;
    if (!beginResolution(requestKey)) return;

    setMarkDifferentLoading(true);
    setMarkDifferentError(null);
    try {
      const result = await eventsApi.markDifferent(evA.id, evB.id);
      const promotedCount = result.promoted_event_ids?.length || 0;
      await onActionSuccess?.(
        promotedCount > 0
          ? `Marked as different and promoted ${promotedCount} pending event${promotedCount === 1 ? "" : "s"}`
          : "Marked as different",
        { editChoices: [evA, evB] }
      );
    } catch (e) {
      setMarkDifferentError(e?.response?.data?.detail || "Could not mark events as different");
    } finally {
      setMarkDifferentLoading(false);
      endResolution(requestKey);
    }
  };

  return (
    <DuplicateReviewCard
      title={
        <>
            {displayLabel(evA.preferred_title, evA.title)}{" "}
            <span className="text-muted-foreground font-normal">vs</span>{" "}
            {displayLabel(evB.preferred_title, evB.title)}
        </>
      }
      reason={group.reason}
      score={group.similarity_score}
      scoreDestructiveAt={85}
      scoreWarningAt={70}
    >
      <ComparisonGrid left={<EventCard event={evA} />} right={<EventCard event={evB} />} />

          {/* Action bar */}
          <div className="flex flex-col gap-2 pt-1 sm:flex-row">
            <Button
              variant="outline"
              size="sm"
              className="gap-1.5"
              onClick={openMergeModal}
              disabled={resolutionBusy || markDifferentLoading}
            >
              <Merge className="h-3.5 w-3.5" />
              Merge
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="gap-1.5 text-muted-foreground sm:ml-auto"
              onClick={handleMarkDifferent}
              disabled={resolutionBusy || markDifferentLoading || mergeModalOpen || mergeLoading}
            >
              {markDifferentLoading ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <X className="h-3.5 w-3.5" />
              )}
              Mark as different
            </Button>
          </div>
          {markDifferentError && <p className="text-sm text-destructive">{markDifferentError}</p>}

      {/* ── Merge modal ── */}
      <Modal open={mergeModalOpen} onOpenChange={handleMergeModalOpenChange}>
        <ModalContent
          title={mergeModalTitle}
          description={mergeModalDescription}
          onEscapeKeyDown={(event) => { if (mergeLoading) event.preventDefault(); }}
          onPointerDownOutside={(event) => { if (mergeLoading) event.preventDefault(); }}
          closeDisabled={mergeLoading}
        >
          {mergeProgressCopy && (
            <div className="flex min-h-48 flex-col items-center justify-center text-center" role="status" aria-live="polite">
              <Loader2 className="mb-4 h-9 w-9 animate-spin text-primary" />
              <p className="font-medium text-foreground">{mergeProgressCopy.status}</p>
              <p className="mt-1 max-w-sm text-sm text-muted-foreground">{mergeProgressCopy.detail}</p>
            </div>
          )}

          {mergePhase === "open-failed" && (
            <>
              <div className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
                {mergeError}
              </div>
              <ModalFooter className="mt-4">
                <Button variant="outline" onClick={() => handleMergeModalOpenChange(false)}>
                  Close
                </Button>
                <Button onClick={() => handleOpenMergedEvent()}>
                  <Eye className="mr-2 h-4 w-4" />
                  Retry opening editor
                </Button>
              </ModalFooter>
            </>
          )}

          {mergePhase === "idle" && mergeStep === "pick" && (
            <>
              <div className="mt-2 grid grid-cols-1 items-stretch gap-4 sm:grid-cols-2">
                <EventCard
                  event={evA}
                  isSelected={canonicalId === evA.id}
                  onSelect={handleCanonicalSelect}
                  selectLabel="Keep this one (canonical)"
                  selectDisabled={previewLoading}
                />
                <EventCard
                  event={evB}
                  isSelected={canonicalId === evB.id}
                  onSelect={handleCanonicalSelect}
                  selectLabel="Keep this one (canonical)"
                  selectDisabled={previewLoading}
                />
              </div>
              <div className="mt-4">
                <OnlyMarkDuplicateToggle
                  checked={!mergeOccurrences}
                  onChange={(onlyMark) => setMergeOccurrences(!onlyMark)}
                  disabled={previewLoading}
                />
              </div>
            </>
          )}

          {mergePhase === "idle" && mergeStep === "preview" && (
            <>
              <MergePreview preview={preview} occurrenceName={editedOccurrenceName} />
              {preview?.merged_into_source > 0 && (
                // Merging it chains duplicate_of (#667): those merges can then only be undone after this one.
                <p className="mt-4 rounded-lg border border-yellow-200 bg-yellow-50 px-3 py-2 text-xs text-yellow-800">
                  The event being merged away already holds {preview.merged_into_source} event(s) merged into
                  it. After this merge, undoing any of those waits until this merge is undone first.
                </p>
              )}
              {mergeOccurrences && preview && preview.occurrences_from_source > 0 && (
                <div className="mt-4">
                  <label className="block text-sm font-medium text-foreground mb-1">
                    Occurrence label
                  </label>
                  <input
                    type="text"
                    value={editedOccurrenceName}
                    onChange={(e) => setEditedOccurrenceName(e.target.value)}
                    maxLength={100}
                    placeholder="e.g. VIP, Early Bird — leave blank for none"
                    className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
                  />
                  <p className="mt-1 text-xs text-muted-foreground">
                    {preview.suggested_occurrence_name
                      ? "Pre-filled from the title difference. "
                      : "No title difference to suggest. "}
                    Unnamed occurrences at the same date and time from the same provider are shown as one on the
                    public site, so name a separate tier (VIP, Parking…) or clear this if it is the same showing.
                  </p>
                </div>
              )}
            </>
          )}

          {mergePhase === "idle" && mergeError && <p className="mt-3 text-sm text-destructive">{mergeError}</p>}

          {mergePhase === "idle" && (
            <ModalFooter className="mt-4">
              {mergeStep === "pick" ? (
                <Button onClick={handleLoadPreview} disabled={!canonicalId || previewLoading}>
                  {previewLoading ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <Eye className="mr-2 h-4 w-4" />
                  )}
                  Preview merge
                </Button>
              ) : (
                <>
                  <Button variant="outline" onClick={() => setMergeStep("pick")} disabled={mergeLoading}>
                    Back
                  </Button>
                  <Button onClick={handleConfirmMerge} disabled={mergeLoading}>
                    {mergeLoading ? (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    ) : (
                      <Merge className="mr-2 h-4 w-4" />
                    )}
                    Confirm merge
                  </Button>
                </>
              )}
            </ModalFooter>
          )}
        </ModalContent>
      </Modal>
    </DuplicateReviewCard>
  );
}

export default function DuplicatesView({ onQueueChange, onEditEvent }) {
  const [innerTab, setInnerTab] = useState("review");
  const [groups, setGroups] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, page_size: 20, total: 0, total_pages: 0 });
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [scanned, setScanned] = useState(false);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState(null);
  const [pendingOnly, setPendingOnly] = useState(true);
  const { searchInput, setSearchInput, search, submitSearch, clearSearch } = useSearchFilter();
  const [cityFilter, setCityFilter] = useState("");
  const [differentEditChoices, setDifferentEditChoices] = useState([]);
  const [differentEditError, setDifferentEditError] = useState(null);
  const [differentEditLoadingId, setDifferentEditLoadingId] = useState(null);
  const [activeResolutionKey, setActiveResolutionKey] = useState(null);
  const duplicateRequestRef = useRef(0);
  const queueCountRequestRef = useRef(0);
  const differentEditRequestRef = useRef(null);
  const activeResolutionRef = useRef(null);
  const queueViewRef = useRef({ page, search, cityFilter });
  const toastTimeoutRef = useRef(null);
  queueViewRef.current = { page, search, cityFilter };

  const showToast = (msg) => {
    if (toastTimeoutRef.current) clearTimeout(toastTimeoutRef.current);
    setToast(msg);
    toastTimeoutRef.current = setTimeout(() => {
      setToast(null);
      toastTimeoutRef.current = null;
    }, 4000);
  };

  const loadStoredDuplicates = useCallback(async (pageNum = 1, searchTerm = "", city = "") => {
    const requestId = ++duplicateRequestRef.current;
    const queueCountRequestId = !city && !searchTerm ? ++queueCountRequestRef.current : null;
    setLoading(true);
    setError(null);
    setScanned(false);
    try {
      const data = await eventsApi.getPossibleDuplicates(pageNum, 20, searchTerm || null, city || null);
      if (requestId !== duplicateRequestRef.current) return;

      const nextPagination = data.pagination || { page: 1, page_size: 20, total: 0, total_pages: 1 };
      setGroups(data.groups || []);
      setPagination(nextPagination);
      if (queueCountRequestId === queueCountRequestRef.current) {
        onQueueChange?.(data.total_groups || 0);
      }
      setScanned(true);

      if (pageNum > 1 && pageNum > (nextPagination.total_pages || 1)) {
        setPage(Math.max(1, nextPagination.total_pages || 1));
      }
    } catch (e) {
      if (requestId !== duplicateRequestRef.current) return;
      setError(e?.response?.data?.detail || "Could not load stored duplicate review pairs.");
    } finally {
      if (requestId === duplicateRequestRef.current) setLoading(false);
    }
  }, [onQueueChange]);

  useEffect(() => {
    loadStoredDuplicates(page, search, cityFilter);
  }, [loadStoredDuplicates, page, search, cityFilter]);

  useEffect(() => () => {
    duplicateRequestRef.current += 1;
    queueCountRequestRef.current += 1;
    activeResolutionRef.current = null;
    if (toastTimeoutRef.current) clearTimeout(toastTimeoutRef.current);
  }, []);

  const isResolutionBusy = useCallback(() => Boolean(activeResolutionRef.current), []);

  const beginResolution = useCallback((requestKey) => {
    if (activeResolutionRef.current) return false;

    activeResolutionRef.current = requestKey;
    setActiveResolutionKey(requestKey);
    return true;
  }, []);

  const endResolution = useCallback((requestKey) => {
    if (activeResolutionRef.current !== requestKey) return;

    activeResolutionRef.current = null;
    setActiveResolutionKey(null);
  }, []);

  const refreshGlobalQueueCount = useCallback(async () => {
    const requestId = ++queueCountRequestRef.current;
    try {
      const data = await eventsApi.getPossibleDuplicates(1, 1);
      if (requestId === queueCountRequestRef.current) {
        onQueueChange?.(data.total_groups || 0);
      }
    } catch {
      // Queue count is best-effort; the filtered review list remains authoritative.
    }
  }, [onQueueChange]);

  const handleCityChange = (city) => {
    setPage(1);
    setCityFilter(city);
  };

  const handleActionSuccess = async (msg, { editChoices = null } = {}) => {
    showToast(msg);
    if (editChoices) {
      setDifferentEditError(null);
      setDifferentEditChoices(editChoices);
    }
    const currentView = queueViewRef.current;
    await loadStoredDuplicates(currentView.page, currentView.search, currentView.cityFilter);
    if (currentView.cityFilter || currentView.search) {
      await refreshGlobalQueueCount();
    }
  };

  const openDifferentEventEditor = async (eventId) => {
    if (differentEditRequestRef.current) return;

    differentEditRequestRef.current = eventId;
    setDifferentEditLoadingId(eventId);
    setDifferentEditError(null);
    try {
      if (!onEditEvent) throw new Error("Event editor is unavailable");
      await onEditEvent(eventId);
    } catch (e) {
      setDifferentEditError(e?.response?.data?.detail || "The event editor could not be opened.");
    } finally {
      differentEditRequestRef.current = null;
      setDifferentEditLoadingId(null);
    }
  };

  const closeDifferentEditChooser = () => {
    if (differentEditRequestRef.current) return;
    setDifferentEditChoices([]);
    setDifferentEditError(null);
  };

  const handleSearchSubmit = (e) => {
    submitSearch(e);
    setPage(1);
  };

  const handleSearchClear = () => {
    clearSearch();
    setPage(1);
  };

  const visibleGroups = pendingOnly ? groups.filter(hasPendingEvent) : groups;
  const hiddenByPendingFilter = pendingOnly ? groups.length - visibleGroups.length : 0;

  return (
    <div className="space-y-6">
      {/* Inner tab switcher */}
      <div className="flex items-center gap-0.5 rounded-md border border-border bg-muted/40 p-0.5 w-fit">
        <button
          onClick={() => setInnerTab("review")}
          className={`rounded px-3 py-1 text-sm font-medium transition-colors ${
            innerTab === "review"
              ? "bg-background text-foreground shadow-sm"
              : "text-muted-foreground hover:text-foreground"
          }`}
        >
          Open pairs
        </button>
        <button
          onClick={() => setInnerTab("flagged")}
          className={`rounded px-3 py-1 text-sm font-medium transition-colors ${
            innerTab === "flagged"
              ? "bg-background text-foreground shadow-sm"
              : "text-muted-foreground hover:text-foreground"
          }`}
        >
          Merged events
        </button>
      </div>

      {innerTab === "flagged" && <FlaggedDuplicatesView />}

      {innerTab === "review" && <>
      {/* Toolbar */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <h2 className="text-lg font-semibold">Open duplicate pairs</h2>
          <p className="text-sm text-muted-foreground">
            Pipeline-detected duplicate pairs waiting for an admin decision before event classification and promotion.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2 sm:shrink-0">
          <CitySelect value={cityFilter} onChange={handleCityChange} />

          <SearchFilterForm
            value={searchInput}
            onChange={setSearchInput}
            onSubmit={handleSearchSubmit}
            onClear={handleSearchClear}
            placeholder="Search by event title or ID"
            inputClassName="sm:w-56"
          />

          <label className="flex h-9 items-center gap-2 rounded-md border border-border bg-background px-3 text-sm font-medium text-foreground transition-colors hover:bg-muted cursor-pointer select-none">
            <input
              type="checkbox"
              checked={pendingOnly}
              onChange={(e) => setPendingOnly(e.target.checked)}
              className="h-4 w-4 rounded border-border accent-primary"
            />
            <span>Pending pairs only</span>
          </label>

          {loading && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
        </div>
      </div>

      {error && (
        <div className="rounded-lg border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-destructive">
          {error}
        </div>
      )}

      {toast && (
        <div className="rounded-lg border border-green-300 bg-green-50 px-4 py-3 text-sm text-green-800 flex items-center gap-2">
          <CheckCircle2 className="h-4 w-4 shrink-0" />
          {toast}
        </div>
      )}

      <Modal
        open={differentEditChoices.length > 0}
        onOpenChange={(open) => { if (!open) closeDifferentEditChooser(); }}
      >
        <ModalContent
          title="Events marked as different"
          description="Both events remain separate. Choose either event to open it in the editor."
          closeDisabled={Boolean(differentEditLoadingId)}
          onEscapeKeyDown={(event) => { if (differentEditLoadingId) event.preventDefault(); }}
          onPointerDownOutside={(event) => { if (differentEditLoadingId) event.preventDefault(); }}
        >
          {differentEditChoices.length === 2 && (
            <div className="mt-2">
              <ComparisonGrid
                left={(
                  <EventCard
                    event={differentEditChoices[0]}
                    onSelect={openDifferentEventEditor}
                    selectLabel="Open in editor"
                    selectLoading={differentEditLoadingId === differentEditChoices[0].id}
                    selectDisabled={Boolean(differentEditLoadingId)}
                  />
                )}
                right={(
                  <EventCard
                    event={differentEditChoices[1]}
                    onSelect={openDifferentEventEditor}
                    selectLabel="Open in editor"
                    selectLoading={differentEditLoadingId === differentEditChoices[1].id}
                    selectDisabled={Boolean(differentEditLoadingId)}
                  />
                )}
                separator="or"
              />
            </div>
          )}
          {differentEditError && (
            <div className="mt-4 rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
              {differentEditError}
            </div>
          )}
          <ModalFooter className="mt-4">
            <Button variant="outline" onClick={closeDifferentEditChooser} disabled={Boolean(differentEditLoadingId)}>
              Close
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {loading && (
        <div className="rounded-xl border border-dashed border-border py-16 text-center text-muted-foreground">
          <Loader2 className="mx-auto mb-3 h-8 w-8 animate-spin" />
          <p className="font-medium">Loading open duplicate pairs…</p>
        </div>
      )}

      {!loading && scanned && (
        <>
          <div className="text-sm text-muted-foreground">
            {visibleGroups.length === 0
              ? pendingOnly
                ? "No pending open duplicate pairs stored"
                : "No open duplicate pairs stored"
              : `${visibleGroups.length} pair${visibleGroups.length !== 1 ? "s" : ""} on this page · ${pagination.total} total`}
            {hiddenByPendingFilter > 0 && <span> · {hiddenByPendingFilter} hidden by pending filter</span>}
          </div>

          {visibleGroups.length > 0 && (
            <div className="space-y-3">
              {visibleGroups.map((group) => (
                <DuplicateGroup
                  key={`${group.events[0]?.id}-${group.events[1]?.id}`}
                  group={group}
                  onActionSuccess={handleActionSuccess}
                  onOpenEventEdit={onEditEvent}
                  resolutionBusy={Boolean(activeResolutionKey)}
                  isResolutionBusy={isResolutionBusy}
                  beginResolution={beginResolution}
                  endResolution={endResolution}
                />
              ))}
            </div>
          )}

          <Pagination page={page} totalPages={pagination.total_pages} onPageChange={setPage} />

          {visibleGroups.length === 0 && (
            <div className="rounded-xl border border-dashed border-border py-16 text-center text-muted-foreground">
              <CheckCircle2 className="mx-auto mb-3 h-8 w-8 text-green-500" />
              <p className="font-medium">
                {pendingOnly && hiddenByPendingFilter > 0 ? "No pending pairs" : "All clear"}
              </p>
              <p className="text-sm mt-1">
                {pendingOnly && hiddenByPendingFilter > 0
                  ? "All hidden groups are active-only."
                  : "No suspicious duplicates detected."}
              </p>
            </div>
          )}
        </>
      )}

      {!scanned && !loading && (
        <div className="rounded-xl border border-dashed border-border py-20 text-center text-muted-foreground">
          <CheckCircle2 className="mx-auto mb-3 h-8 w-8 text-green-500" />
          <p className="font-medium">No duplicate results loaded</p>
          <p className="text-sm mt-1">
            Stored pipeline review pairs load automatically.
          </p>
        </div>
      )}
      </>}
    </div>
  );
}
